import React, {useState, useCallback, forwardRef} from 'react';
import { useSelector } from 'umi';
import UploadTableFile from '../UploadTableFile';
import SaveTableInfo from '../SaveTableInfo';
import { TState } from '../UploadTableFile/types';
import { IUploadFileRef } from '../UploadFile/types';
import { IUploadModalBodyProps, IGameState, ITableInfo } from './types';
import './index.scss';

const useGameCode = (): string | undefined => {
  const { gameCode } = useSelector((state: IGameState) => ({ 
    gameCode: state.datalab?.gameCode 
  }));
  return gameCode;
};

const UploadModalBody = forwardRef<IUploadFileRef, IUploadModalBodyProps>(({ 
  style, allStateRef,
  onClose, 
  onSaveSuccess 
}, ref) => {
  const gameCode = useGameCode();
  const [currentStep, setCurrentStep] = useState<number>(1);
  
  // 计算SaveTableInfo的默认值
  const calculateDefaultFormData = useCallback((
    checkedSheetNames: string[], 
    previousFormData: ITableInfo[] | null
  ): ITableInfo[] => {
    if (!previousFormData) {
      // 没有之前的数据，全部重置
      return checkedSheetNames.map(sheetName => ({
        name: sheetName,
        description: '',
        sheet_name: sheetName
      }));
    }
    
    // 有之前的数据，智能匹配
    return checkedSheetNames.map(sheetName => {
      const previousItem = previousFormData.find(item => item.sheet_name === sheetName);
      if (previousItem) {
        // 找到匹配的，维持之前的数据
        return { ...previousItem };
      } 
      // 没有匹配的，重置为新数据
      return {
        name: sheetName,
        description: '',
        sheet_name: sheetName
      };
    });
  }, []);
  
  // Next Step按钮点击事件
  const handleNextStep = (uploadState: TState): void => {
    // 1. 从UploadTableFile组件获取状态
    allStateRef.current.uploadTableFile = uploadState;
    
    // 2. 计算SaveTableInfo的默认值
    const defaultFormData = calculateDefaultFormData(
      uploadState?.checkedSheets || [],
      allStateRef.current.saveTableInfo
    );
    
    // 3. 存储到allStateRef中
    allStateRef.current.saveTableInfo = defaultFormData;
    
    // 4. 改变step状态
    setCurrentStep(2);
  };
  
  // Prev Step按钮点击事件
  const handlePrevStep = (formData: ITableInfo[]): void => {
    // 1. 从SaveTableInfo组件获取状态
    allStateRef.current.saveTableInfo = formData;
    // 2. 改变step状态
    setCurrentStep(1);
  };
  
  return (
    <div className="upload-modal-body" style={style}>
      {currentStep === 1 ? (
        <UploadTableFile
          ref={ref}
          defaultState={allStateRef.current?.uploadTableFile || undefined}
          gameCode={gameCode || ''}
          onNextStep={handleNextStep}
          onDeleteFile={() => {
            allStateRef.current = {
              uploadTableFile: null,
              saveTableInfo: null
            };
          }}
        />
      ) : (
        <SaveTableInfo
          defaultFormData={allStateRef.current.saveTableInfo || undefined}
          uploadTableFile={allStateRef.current.uploadTableFile!}
          onPrevStep={handlePrevStep}
          onSubmitSuccess={() => {
            onClose?.();
            onSaveSuccess?.(); // 新增：调用保存成功回调
            allStateRef.current = {
              uploadTableFile: null,
              saveTableInfo: null
            };
          }}
          gameCode={gameCode || ''}
        />
      )}
    </div>
  );
});

export default UploadModalBody;
